import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Remembered collapse state for the items of a schema-form array that opts in
 * via `rememberCollapsed: true` on its `form-array` element schema.
 *
 * The collapsed item keys are kept in localStorage, scoped to the record the
 * form renders and to the array's data path, so the state survives the form
 * unmounting (e.g. a dashboard switching between its view and its config
 * editor) and page reloads. Items are keyed by the value of the schema's
 * `itemKeyProperty` (e.g. a dashboard widget's `uid`) when present, so the
 * state follows an item when the list is reordered; otherwise by index.
 *
 * Without a record id (a create screen, a flow input form) nothing is stored
 * and every item renders expanded.
 */

const STORAGE_PREFIX = "cloudomation.schemaFormCollapsed";

export const collapseStorageKey = (recordId: string, dataPath: string[]): string =>
    `${STORAGE_PREFIX}.${recordId}.${dataPath.join(".")}`;

export const readCollapsedKeys = (storageKey: string): string[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
        return Array.isArray(parsed) ? parsed.filter((key) => typeof key === "string") : [];
    } catch {
        return [];
    }
};

const writeCollapsedKeys = (storageKey: string, keys: string[]): void => {
    try {
        if (keys.length === 0) {
            localStorage.removeItem(storageKey);
        } else {
            localStorage.setItem(storageKey, JSON.stringify(keys));
        }
    } catch {
        /* ignore storage errors (e.g. private mode) — state stays in memory */
    }
};

/** Stable key of an array item: its `itemKeyProperty` value, else `#<index>`. */
export const arrayItemCollapseKey = (
    item: unknown,
    index: number,
    itemKeyProperty?: string,
): string => {
    if (itemKeyProperty && item && typeof item === "object") {
        const value = (item as Record<string, unknown>)[itemKeyProperty];
        if (typeof value === "string" && value !== "") return value;
        if (typeof value === "number") return String(value);
    }
    return `#${index}`;
};

export interface RememberedCollapse {
    /** False when remembering is inactive (opt-out or no record id). */
    isActive: boolean;
    isCollapsed: (itemKey: string) => boolean;
    setCollapsed: (itemKey: string, collapsed: boolean) => void;
}

export const useRememberedCollapse = (storageKey: string | null): RememberedCollapse => {
    const [state, setState] = useState<{ key: string | null; collapsed: string[] }>(() => ({
        key: storageKey,
        collapsed: storageKey ? readCollapsedKeys(storageKey) : [],
    }));
    // Re-read when the scope changes (another record or array path).
    useEffect(() => {
        setState((previous) =>
            previous.key === storageKey
                ? previous
                : { key: storageKey, collapsed: storageKey ? readCollapsedKeys(storageKey) : [] },
        );
    }, [storageKey]);

    const collapsed = state.collapsed;
    const isCollapsed = useCallback((itemKey: string) => collapsed.includes(itemKey), [collapsed]);

    const setCollapsed = useCallback(
        (itemKey: string, isItemCollapsed: boolean) => {
            if (!storageKey) return;
            setState((previous) => {
                const current = previous.key === storageKey ? previous.collapsed : [];
                const without = current.filter((key) => key !== itemKey);
                const next = isItemCollapsed ? [...without, itemKey] : without;
                writeCollapsedKeys(storageKey, next);
                return { key: storageKey, collapsed: next };
            });
        },
        [storageKey],
    );

    return useMemo(
        () => ({ isActive: storageKey !== null, isCollapsed, setCollapsed }),
        [storageKey, isCollapsed, setCollapsed],
    );
};
